import React, { useState } from "react";
import {
  Layout,
  Card,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Divider,
  Row,
  Col,
  Tabs,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { PageHeader } from "@ant-design/pro-layout";

const { Content } = Layout;
const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface ValidationResult {
  isValid: boolean;
  type?: "VAST" | "HTML" | "MRAID";
  errors: string[];
  warnings: string[];
}

export const CreativeValidatorPage: React.FC = () => {
  const [input, setInput] = useState("");
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [jsonSafeOutput, setJsonSafeOutput] = useState("");

  const validateVAST = (xml: string): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if it contains VAST tag
    if (!xml.includes("<VAST")) {
      errors.push("Missing <VAST> root element");
    }

    // Check for required VAST version
    if (!xml.match(/<VAST\s+version=/)) {
      warnings.push("VAST version attribute is recommended");
    }

    // Check for Ad element
    if (!xml.includes("<Ad")) {
      errors.push("Missing <Ad> element");
    }

    // Check for InLine or Wrapper
    if (!xml.includes("<InLine") && !xml.includes("<Wrapper")) {
      errors.push("Missing <InLine> or <Wrapper> element");
    }

    // Check for MediaFiles in video ads
    if (xml.includes("<Linear") && !xml.includes("<MediaFiles")) {
      errors.push("Linear ad missing <MediaFiles> element");
    }

    // Check for proper XML structure
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, "text/xml");
      const parseError = doc.querySelector("parsererror");
      if (parseError) {
        errors.push("Invalid XML structure: " + parseError.textContent);
      }
    } catch (e: any) {
      errors.push("XML parsing error: " + e.message);
    }

    return {
      isValid: errors.length === 0,
      type: "VAST",
      errors,
      warnings,
    };
  };

  const validateHTML = (html: string): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for basic HTML structure
    const hasHtml = html.toLowerCase().includes("<html");
    const hasBody = html.toLowerCase().includes("<body");
    const hasHead = html.toLowerCase().includes("<head");

    if (!hasHtml && !hasBody) {
      warnings.push("Missing HTML structure tags (html, body)");
    }

    // Check for unclosed tags (basic check)
    const openTags = html.match(/<(\w+)[^>]*>/g) || [];
    const closeTags = html.match(/<\/(\w+)>/g) || [];

    if (openTags.length > closeTags.length + 5) {
      warnings.push("Possible unclosed tags detected");
    }

    // Check for script tags
    if (html.includes("<script")) {
      warnings.push("Contains script tags - ensure they're properly escaped");
    }

    // Check for MRAID
    const isMRAID = html.includes("mraid.js") || html.includes("mraid.getState");

    return {
      isValid: errors.length === 0,
      type: isMRAID ? "MRAID" : "HTML",
      errors,
      warnings,
    };
  };

  const detectAndValidate = (text: string): ValidationResult => {
    if (!text.trim()) {
      return {
        isValid: false,
        errors: ["Input is empty"],
        warnings: [],
      };
    }

    // Detect type based on content
    const trimmedText = text.trim();

    if (trimmedText.includes("<VAST")) {
      return validateVAST(text);
    } else if (
      trimmedText.includes("<html") ||
      trimmedText.includes("<div") ||
      trimmedText.includes("<script")
    ) {
      return validateHTML(text);
    } else {
      return {
        isValid: false,
        errors: ["Unable to detect content type (VAST, HTML, or MRAID)"],
        warnings: [],
      };
    }
  };

  const convertToJsonSafe = (text: string): string => {
    // Remove all line breaks and extra whitespace
    let result = text
      .replace(/\r\n/g, " ")
      .replace(/\n/g, " ")
      .replace(/\r/g, " ")
      .replace(/\t/g, " ");

    // Replace multiple spaces with single space
    result = result.replace(/\s+/g, " ");

    // Trim
    result = result.trim();

    // Escape double quotes
    result = result.replace(/"/g, '\\"');

    return result;
  };

  const handleValidate = () => {
    const result = detectAndValidate(input);
    setValidationResult(result);

    if (result.isValid || result.errors.length === 0) {
      const jsonSafe = convertToJsonSafe(input);
      setJsonSafeOutput(jsonSafe);
    } else {
      setJsonSafeOutput("");
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleCopyAsJson = () => {
    const jsonString = JSON.stringify({ creative: jsonSafeOutput }, null, 2);
    navigator.clipboard.writeText(jsonString);
  };

  return (
    <Layout>
      <Content>
        <PageHeader title="Creative Validator" subTitle="Validate and convert VAST, HTML, and MRAID creatives">
          <Paragraph>
            This tool validates VAST XML tags and HTML/MRAID creatives, then
            converts them to JSON-safe single-line strings.
          </Paragraph>
        </PageHeader>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Input" extra={<Text type="secondary">Paste your VAST, HTML, or MRAID tag</Text>}>
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                <TextArea
                  rows={15}
                  placeholder="Paste your VAST XML or HTML/MRAID creative here..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  style={{ fontFamily: "monospace", fontSize: "12px" }}
                />
                <Button type="primary" onClick={handleValidate} block>
                  Validate & Convert
                </Button>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {validationResult && (
                <Card
                  title={
                    <Space>
                      {validationResult.isValid ? (
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                      )}
                      Validation Result
                    </Space>
                  }
                >
                  {validationResult.type && (
                    <Alert
                      message={`Detected Type: ${validationResult.type}`}
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {validationResult.isValid && (
                    <Alert
                      message="Validation Passed"
                      description="The creative is valid and ready to use."
                      type="success"
                      showIcon
                    />
                  )}

                  {validationResult.errors.length > 0 && (
                    <Alert
                      message="Errors"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {validationResult.errors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      }
                      type="error"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}

                  {validationResult.warnings.length > 0 && (
                    <Alert
                      message="Warnings"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {validationResult.warnings.map((warning, idx) => (
                            <li key={idx}>{warning}</li>
                          ))}
                        </ul>
                      }
                      type="warning"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </Card>
              )}

              {jsonSafeOutput && (
                <Card
                  title="JSON-Safe Output"
                  extra={
                    <Space>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(jsonSafeOutput)}
                        size="small"
                      >
                        Copy String
                      </Button>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={handleCopyAsJson}
                        size="small"
                        type="primary"
                      >
                        Copy as JSON
                      </Button>
                    </Space>
                  }
                >
                  <TextArea
                    rows={10}
                    value={jsonSafeOutput}
                    readOnly
                    style={{ fontFamily: "monospace", fontSize: "11px" }}
                  />
                  <Divider />
                  <Text type="secondary">
                    This output is safe to use in JSON files. All line breaks
                    are removed and quotes are properly escaped.
                  </Text>
                </Card>
              )}
            </Space>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};
